import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TopNav from '../components/TopNav';
import GradientButton from '../components/GradientButton';
import { colors } from '../theme';

interface JobData {
  status: string;
  jobType: string;
  category: string;
  issueDescription: string;
  serviceAddress: string;
  techName?: string;
  techId?: string;
  etaMinutes?: number;
  createdAt?: any;
  timeline?: {
    acceptedAt?: any;
    arrivedAt?: any;
    completedAt?: any;
  };
  pricing?: {
    hourlyRate?: number;
    serviceFee?: number;
  };
  aiDiagnosis?: string;
  isEmergency?: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; emoji: string; color: string; step: number }> = {
  pending: { label: 'Finding your technician…', emoji: '🔍', color: colors.warning, step: 0 },
  dispatched: { label: 'Finding your technician…', emoji: '🔍', color: colors.warning, step: 0 },
  pending_tech_acceptance: { label: 'Waiting for tech to accept…', emoji: '⏳', color: colors.warning, step: 0 },
  accepted: { label: 'Tech accepted! On their way', emoji: '✅', color: colors.success, step: 1 },
  en_route: { label: 'Your tech is on the way', emoji: '🚗', color: colors.success, step: 2 },
  arrived: { label: 'Tech has arrived!', emoji: '🎉', color: colors.success, step: 3 },
  in_progress: { label: 'Job in progress', emoji: '🔧', color: colors.primary, step: 4 },
  completed: { label: 'Job complete!', emoji: '✅', color: colors.success, step: 5 },
  couldnt_fix: { label: "Tech couldn't fix it — no charge", emoji: '😔', color: colors.error, step: 5 },
  awaiting_customer_decision: { label: 'Action required', emoji: '⚠️', color: colors.warning, step: 5 },
  cancelled: { label: 'Job cancelled', emoji: '❌', color: colors.error, step: 5 },
};

const STEPS = ['Finding', 'Accepted', 'En Route', 'Arrived', 'In Progress'];

const CATEGORY_LABELS: Record<string, string> = {
  wifi_networking: 'Wi-Fi & Networking',
  printer_peripherals: 'Printer & Peripherals',
  virus_removal: 'Virus Removal',
  smart_home: 'Smart Home',
  tv_device_setup: 'TV & Device Setup',
  social_media: 'Social Media',
  pc_setup: 'PC Setup',
  social_media_account_recovery: 'Account Recovery',
};

export default function JobStatusPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!jobId) return;
    const unsub = onSnapshot(doc(db, 'jobs', jobId), (snap) => {
      if (snap.exists()) {
        setJob(snap.data() as JobData);
        setLoading(false);
        if (snap.data()?.status === 'completed') {
          navigate(`/review/${jobId}`, { replace: true });
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [jobId, navigate]);

  // Elapsed timer for in-progress jobs
  useEffect(() => {
    if (job?.status === 'in_progress' || job?.status === 'arrived') {
      const interval = setInterval(() => setElapsed((e) => e + 1), 60000);
      return () => clearInterval(interval);
    }
  }, [job?.status]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${colors.primary}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
              margin: '0 auto 12px',
            }}
          />
          <p style={{ color: colors.textSecondary }}>Loading job…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!job) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: colors.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2 style={{ color: colors.textPrimary }}>Job not found</h2>
        <GradientButton onClick={() => navigate('/home')} style={{ marginTop: 20 }}>
          Go Home
        </GradientButton>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
  const currentStep = statusInfo.step;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 40,
      }}
    >
      <TopNav title="Job Status" showBack />

      <div style={{ padding: '16px 16px 0' }}>
        {/* Status header */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 20,
            padding: '24px 20px',
            textAlign: 'center',
            marginBottom: 16,
            border: `1px solid ${statusInfo.color}33`,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: `${statusInfo.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              margin: '0 auto 12px',
            }}
          >
            {statusInfo.emoji}
          </div>
          <h2 style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>
            {statusInfo.label}
          </h2>
          {job.etaMinutes && job.status === 'en_route' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginTop: 4,
              }}
            >
              <span style={{ color: colors.success, fontSize: 14 }}>🕐</span>
              <span style={{ color: colors.success, fontSize: 14, fontWeight: 600 }}>
                Arriving in ~{job.etaMinutes} min
              </span>
            </div>
          )}

          {/* Pulsing dot for active statuses */}
          {['pending', 'dispatched', 'pending_tech_acceptance', 'en_route', 'accepted'].includes(
            job.status
          ) && (
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: statusInfo.color,
                margin: '12px auto 0',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          )}
        </div>

        {/* Progress steps */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 16,
            padding: '16px 12px',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {STEPS.map((step, i) => (
              <React.Fragment key={step}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    flex: 'none',
                    minWidth: 54,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: i <= currentStep ? colors.primary : 'rgba(26,10,46,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.textPrimary,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {i < currentStep ? '✓' : i === currentStep ? '●' : i + 1}
                  </div>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: i <= currentStep ? colors.primary : colors.textSecondary,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {step}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      background: i < currentStep ? colors.primary : 'rgba(26,10,46,0.1)',
                      marginTop: -14,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Tech info */}
        {job.techName && (
          <div
            style={{
              background: colors.surface,
              borderRadius: 16,
              padding: '14px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(102,51,153,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                color: colors.primary,
              }}
            >
              {job.techName.charAt(0)}
            </div>
            <div>
              <div style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15 }}>{job.techName}</div>
              <div style={{ color: colors.textSecondary, fontSize: 13 }}>Your technician</div>
            </div>
          </div>
        )}

        {/* Job details */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 16,
            padding: '16px 18px',
            marginBottom: 16,
          }}
        >
          <h3 style={{ color: colors.textPrimary, fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
            Job Details
          </h3>
          {([
            ['Category', CATEGORY_LABELS[job.category] || job.category],
            job.issueDescription ? ['Issue', job.issueDescription] : null,
            job.serviceAddress ? ['Address', job.serviceAddress] : null,
            job.jobType ? ['Type', job.jobType === 'asap' ? '⚡ ASAP' : '📅 Scheduled'] : null,
            job.pricing?.hourlyRate ? ['Rate', `$${job.pricing.hourlyRate}/hr`] : null,
          ] as [string, string][])
            .filter((item): item is [string, string] => item !== null)
            .map(([label, value]) => (
              <div
                key={label as string}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <span style={{ color: colors.textSecondary, fontSize: 13, flexShrink: 0 }}>
                  {label}
                </span>
                <span style={{ color: colors.textPrimary, fontSize: 14, textAlign: 'right' }}>{value}</span>
              </div>
            ))}
        </div>

        {/* AI Diagnosis notice */}
        {job.aiDiagnosis && (
          <div
            style={{
              background: 'rgba(102,51,153,0.1)',
              border: '1px solid rgba(102,51,153,0.25)',
              borderRadius: 14,
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: 18 }}>🧠</span>
            <div>
              <div style={{ color: colors.primary, fontWeight: 700, fontSize: 13, marginBottom: 3 }}>
                Sage AI Analysis Ready
              </div>
              <div style={{ color: colors.textSecondary, fontSize: 12 }}>
                Your tech has received a full diagnosis briefing.
              </div>
            </div>
          </div>
        )}

        {/* Completed state */}
        {job.status === 'completed' && (
          <div
            style={{
              background: 'rgba(48,209,88,0.1)',
              border: '1px solid rgba(48,209,88,0.2)',
              borderRadius: 16,
              padding: '20px',
              marginBottom: 16,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
            <h3 style={{ color: colors.textPrimary, fontSize: 18, margin: '0 0 8px' }}>Issue Resolved!</h3>
            <p style={{ color: colors.textSecondary, fontSize: 14, margin: '0 0 16px' }}>
              How did your technician do?
            </p>
            <GradientButton onClick={() => navigate(`/review/${jobId}`)} fullWidth>
              Rate & Complete Payment
            </GradientButton>
          </div>
        )}

        {/* Cancelled state */}
        {job.status === 'cancelled' && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 16 }}>
              Your card hold has been released — you have not been charged.
            </p>
            <GradientButton onClick={() => navigate('/booking')} fullWidth>
              Book Another Tech
            </GradientButton>
          </div>
        )}

        {/* Couldn't fix state */}
        {job.status === 'couldnt_fix' && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 16 }}>
              You have not been charged.
            </p>
            <GradientButton onClick={() => navigate('/booking')} fullWidth>
              Find Another Tech
            </GradientButton>
          </div>
        )}

        {/* Back to jobs link */}
        <button
          onClick={() => navigate('/my-jobs')}
          style={{
            display: 'block',
            width: '100%',
            padding: '13px',
            background: 'none',
            border: '1.5px solid rgba(26,10,46,0.1)',
            borderRadius: 14,
            color: colors.textSecondary,
            fontSize: 15,
            cursor: 'pointer',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        >
          ← Back to My Jobs
        </button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
